import React, { useState } from 'react';
import { 
  ListItem, 
  ListItemText, 
  Collapse, 
  List, 
  CircularProgress, 
  Typography, 
  Box,
  IconButton,
  Chip
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';
import { useNavigate } from 'react-router-dom';

// Helper function to format date (can be moved to utils if shared)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleString();
  } catch (e) {
    console.error("Error formatting date:", e);
    return 'Invalid Date';
  }
};

function TestGroupItem({ group, isNewGroup = false }) {
  const [expanded, setExpanded] = useState(false);
  const [testRuns, setTestRuns] = useState([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [runsError, setRunsError] = useState(null);
  const navigate = useNavigate();

  const handleToggleExpand = async () => {
    const newExpandedState = !expanded;
    setExpanded(newExpandedState);

    // Fetch runs only if expanding and runs haven't been fetched yet
    if (newExpandedState && testRuns.length === 0 && !isLoadingRuns && !runsError) {
      await fetchTestRuns();
    }
  };

  const fetchTestRuns = async () => {
    setIsLoadingRuns(true);
    setRunsError(null);
    const groupId = group.group_id; // Make sure group_id exists

    if (!groupId) {
        setRunsError("Group ID is missing");
        setIsLoadingRuns(false);
        return;
    }

    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/test_groups/${groupId}/runs`, { headers });

      if (response.data && response.data.test_runs) {
        setTestRuns(response.data.test_runs);
      } else {
        setTestRuns([]);
      }
    } catch (err) {
      console.error(`Error fetching test runs for group ${groupId}:`, err);
      setRunsError(err.message || "Failed to fetch test runs");
      setTestRuns([]);
    } finally {
      setIsLoadingRuns(false);
    }
  };

  const handleTestRunClick = (run) => {
    // Navigate to test replay page with run data
    navigate(`/test/replay/${run.task_id}`, { state: { testRun: run } });
  };

  return (
    <>
      <ListItem 
        button 
        onClick={handleToggleExpand}
        sx={{
          borderRadius: 1,
          mb: 0.5,
          transition: 'all 0.3s ease',
          position: 'relative',
          ...(isNewGroup && {
            backgroundColor: 'rgba(25, 118, 210, 0.1)', 
            boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)',
            animation: 'pulse 2s infinite'
          }),
          '&:hover': {
            backgroundColor: isNewGroup ? 'rgba(25, 118, 210, 0.15)' : 'rgba(255, 255, 255, 0.05)',
          },
          '@keyframes pulse': {
            '0%': {
              boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.5)',
            },
            '70%': {
              boxShadow: '0 0 0 6px rgba(25, 118, 210, 0)',
            },
            '100%': {
              boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
            },
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          width: '100%',
          pr: 1
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ 
              fontSize: '0.95rem',
              fontWeight: 500,
              color: '#fff',
              mb: 0.5
            }}>
              {group.group_id}
              {isNewGroup && (
                <Typography 
                  component="span"
                  sx={{ 
                    ml: 1, 
                    fontSize: '0.75rem', 
                    color: '#90caf9',
                    fontWeight: 400,
                  }}
                >
                  (New)
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label={`Studio: ${group.studio_id}`}
                size="small"
                sx={{
                  backgroundColor: '#252525',
                  color: '#888',
                  fontSize: '0.675rem',
                  height: '20px'
                }}
              />
              <Typography sx={{ 
                color: '#888',
                fontSize: '0.75rem'
              }}>
                Created: {formatDate(group.created_at?.$date || group.created_at)}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            edge="end" 
            sx={{ 
              color: '#666',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ 
          pl: 4, 
          borderLeft: '1px solid #333', 
          ml: 2, 
          mr: 2, 
          mb: 1, 
          backgroundColor: '#1E1E1E',
          borderRadius: 1
        }}> 
          {isLoadingRuns && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {runsError && (
            <Typography sx={{ color: 'error.main', p: 2, textAlign: 'center' }}>
              Error loading runs: {runsError}
            </Typography>
          )}
          {!isLoadingRuns && !runsError && (
            <List disablePadding>
              {testRuns.length > 0 ? (
                testRuns.map((run) => (
                  <ListItem 
                    key={run.run_id || run._id?.$oid} 
                    sx={{
                      borderBottom: '1px solid #252525',
                      '&:last-child': {
                        borderBottom: 'none'
                      },
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                    onClick={() => handleTestRunClick(run)}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 0.5
                      }}>
                        <Typography sx={{ 
                          fontSize: '0.875rem', 
                          color: '#eee',
                          fontWeight: 500
                        }}>
                          Run ID: {run.run_id}
                        </Typography>
                        <Chip
  label={run.status || 'N/A'}
  size="small"
  sx={{
    backgroundColor:
      run.status === 'PASSED' ? 'rgba(62,136,91,0.18)' : // Soft green
      run.status === 'FAILED' ? 'rgba(184,92,92,0.18)' : // Muted red
      run.status === 'SKIPPED' ? 'rgba(184,155,92,0.18)' : // Gentle amber
      run.status === 'COMPLETED' ? 'rgba(46, 78, 65, 0.18)' : // Muted completed
      'rgba(115, 92, 45, 0.18)', // Default fallback
    color: '#d4d4d4',
    border: '1px solid',
    borderColor:
      run.status === 'PASSED' ? '#3e885b' :
      run.status === 'FAILED' ? '#b85c5c' :
      run.status === 'SKIPPED' ? '#b89b5c' :
      run.status === 'COMPLETED' ? '#4d6656' :
      '#72603e',
    fontSize: '0.675rem',
    height: '20px',
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: 0.5,
    px: 1.2,
  }}
/>
                      </Box>
                      <Typography sx={{ 
                        fontSize: '0.75rem', 
                        color: '#999'
                      }}>
                        Test Case: {run.test_case_id} | Created: {formatDate(run.created_at?.$date || run.created_at)}
                      </Typography>
                    </Box>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText 
                    primary="No test runs found for this group." 
                    sx={{ 
                      color: '#888', 
                      textAlign: 'center',
                      fontStyle: 'italic',
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem'
                      }
                    }} 
                  />
                </ListItem>
              )}
            </List>
          )}
        </Box>
      </Collapse>
    </>
  );
}

export default TestGroupItem; 